/* Trending / New & upcoming strip, the region picker and the trending card.
   Moved verbatim from player/src/App.tsx; shared with the launcher. Data access
   and the ping source are injected: the site estimates ping from distance, the
   launcher measures it and can pin a Join action on each card. */
import { memo, useEffect, useRef, useState, type ReactNode } from 'react'
import { Check, ChevronDown, ChevronLeft, ChevronRight, Sprout, TrendingUp } from './Icons'
import { usePopoverPosition } from './FilterControls'
import { flag, fmt, mapLabel } from './format'
import { Sparkline, latencyWord, smoothTrend } from './lib'
import type { DiscoverApi, TrendingItem } from './types'

/* ------------------------------------------------------------------ Trending strip */
// Real, no-mock data: TrendingStrip pulls from /api/trending (servers with live
// queues) and /api/upcoming (servers first seen in the last 30 days that are
// pulling players). Cards render whatever the backend returns. The TrendingItem
// shape is defined in api.ts.

// Hook that loads /api/trending or /api/upcoming. Cached at the module level
// by `${tab}:${country||''}` so flipping tabs and toggling region doesn't
// re-hit the API — the home strip + the Discover page share the same cache.
// Entries carry a timestamp: fresh ones (< 5 min) serve instantly, older ones
// still render immediately but revalidate in the background — "trending now"
// shouldn't freeze for a whole session in a long-lived tab.
const DISCOVER_TTL = 5 * 60 * 1000
const discoverCache: Record<string, { at: number; items: TrendingItem[] }> = {}
export function useDiscover(api: DiscoverApi, tab: DiscoverTab, country: string | null) {
  const key = `${tab}:${country ?? ''}`
  const [items, setItems] = useState<TrendingItem[] | null>(discoverCache[key]?.items ?? null)
  useEffect(() => {
    let alive = true
    const hit = discoverCache[key]
    if (hit) setItems(hit.items)
    else setItems(null)
    if (hit && Date.now() - hit.at < DISCOVER_TTL) return
    const c = country ? [country] : undefined
    const load = tab === 'trending' ? api.trending(c) : api.upcoming(c)
    load.then((r) => { discoverCache[key] = { at: Date.now(), items: r }; if (alive) setItems(r) })
      .catch(() => { if (alive && !hit) setItems([]) })
    return () => { alive = false }   // a slower stale response must not overwrite a newer tab/country
  }, [api, tab, country, key])
  return items
}

// Region picker — clickable chip that lets the user override the IP-detected
// country. Lives next to the "Servers near …" label in the Trending / Upcoming
// header. Selection persists to localStorage in App so it sticks across
// sessions (set to null = "Anywhere", undefined sentinel = "Auto / use IP").
export function RegionPicker({
  region, you, countries, onChange,
}: {
  region: string | null
  you: string | null
  countries: { code: string; count: number }[]
  onChange: (cc: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const pos = usePopoverPosition(open, ref, 260)
  useEffect(() => {
    if (!open) return
    const click = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false) }
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', click)
    document.addEventListener('keydown', key)
    return () => { document.removeEventListener('mousedown', click); document.removeEventListener('keydown', key) }
  }, [open])
  const top = countries.slice(0, 20)
  return (
    <span ref={ref} className="relative inline-block">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="mono text-[11px] inline-flex items-center gap-1 text-[var(--foreground)] hover:opacity-80 transition-opacity rounded">
        {region ? <>near {flag(region)} {region}</> : <>Globally</>}
        <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="listbox" aria-label="Region"
          className="fixed z-40 max-h-[60vh] overflow-y-auto rounded-xl bg-[var(--background-secondary)] p-1.5"
          style={{ top: pos?.top ?? -9999, left: pos?.left ?? 0, width: pos?.width,
                   visibility: pos ? 'visible' : 'hidden', boxShadow: '0 18px 40px rgba(0,0,0,0.55)' }}>
          <button type="button" onClick={() => { onChange(null); setOpen(false) }}
            className="w-full text-left text-sm rounded-lg px-3 py-2 hover:bg-[var(--background-tertiary)] flex items-center justify-between">
            <span>🌍 Anywhere</span>
            {region === null && <Check size={16} className="text-[var(--accent)]" />}
          </button>
          {you && you !== region && (
            <button type="button" onClick={() => { onChange(you); setOpen(false) }}
              className="w-full text-left text-sm rounded-lg px-3 py-2 hover:bg-[var(--background-tertiary)] flex items-center justify-between">
              <span>📍 Detected ({flag(you)} {you})</span>
            </button>
          )}
          <div className="h-px bg-[var(--border)] my-1.5 mx-2" />
          {top.map((c) => (
            <button key={c.code} type="button" onClick={() => { onChange(c.code); setOpen(false) }}
              className="w-full text-left text-sm rounded-lg px-3 py-1.5 hover:bg-[var(--background-tertiary)] flex items-center justify-between">
              <span>{flag(c.code)} {c.code}</span>
              <span className="mono text-[10px] text-[var(--muted)] tabular-nums flex items-center gap-2">
                {fmt(c.count)}
                {region === c.code && <Check size={16} className="text-[var(--accent)]" />}
              </span>
            </button>
          ))}
        </div>
      )}
    </span>
  )
}

// Background art we actually ship in player/public/maps. A map NOT listed here
// keeps the plain card look (that's the intended "default"). To add art for a
// map, drop /maps/<slug>.webp in and add its slug to this set.
const MAP_IMAGES = new Set(['chernarus', 'livonia', 'namalsk', 'sakhal', 'deerisle', 'pripyat', 'hashima'])
// Raw map code -> "/maps/<slug>.webp" when we have art, else null. Routed through
// mapLabel() so every alias of a map (chernarusplus, chernarus, enoch, livonia…)
// resolves to the same canonical image.
export function mapImage(map: string | null | undefined, base = ''): string | null {
  if (!map) return null
  const slug = mapLabel(map).toLowerCase().replace(/[^a-z0-9]+/g, '')
  return MAP_IMAGES.has(slug) ? `${base}/maps/${slug}.webp` : null
}

export interface TrendingCardProps {
  item: TrendingItem
  onOpen: (id: number) => void
  viewerCC: string | null
  index?: number
  // The site estimates ping from distance; the launcher measures it.
  estimatePing?: (viewerCC: string | null, serverCC: string | null) => number | null
  pingFor?: (item: TrendingItem) => number | null
  // Where /maps/<slug>.webp lives ('' on the site, the site origin elsewhere).
  assetBase?: string
  // Launcher only: art resolved locally (bundled files) instead of assetBase.
  imageFor?: (map: string | null) => string | null
  // Launcher only: an action pinned to the card's top-right corner (Join).
  action?: (item: TrendingItem) => ReactNode
}

export function TrendingCard({ item, onOpen, viewerCC, index = 0, estimatePing, pingFor, assetBase = '', imageFor, action }: TrendingCardProps) {
  const bg = imageFor ? imageFor(item.map) : mapImage(item.map, assetBase)
  const fill = item.max_players ? Math.min(1, item.players / item.max_players) : 0
  // Flat card, same surface convention as <FeaturedStrip>: no border, no gradient,
  // no transform on hover — only a background colour change. `h-full flex flex-col`
  // pairs with grid `auto-rows-fr` so every card in a row matches the tallest.
  // Bottom row uses `mt-auto` to anchor to the bottom regardless of name length.
  // `card-rise-in` fades the card in on mount/re-mount, staggered by index.
  const card = (
    <button type="button" onClick={() => onOpen(item.id)}
      className="group card-rise-in relative isolate text-left w-full h-[244px] flex flex-col overflow-hidden
                 rounded-xl bg-[var(--background-secondary)] hover:bg-[var(--background-tertiary)]
                 transition-colors p-4
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
      style={{ animationDelay: `${Math.min(index, 11) * 40}ms` }}>
      {/* Per-map background art (player/public/maps). Two layers sit BEHIND the
          content (negative z within the card's isolated stacking context): a faint
          map image, then a dark gradient running bottom-left -> top-right so the
          name + stats stay readable. Maps with no art skip this and keep the plain
          card surface. */}
      {bg && (
        <>
          {/* Hover: the art comes up to full strength; no motion, no border. */}
          <div aria-hidden className="absolute inset-0 pointer-events-none opacity-80 group-hover:opacity-100 transition-opacity"
               style={{ zIndex: -2, backgroundImage: `url(${bg})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
          <div aria-hidden className="absolute inset-0 pointer-events-none"
               style={{ zIndex: -1, background: 'linear-gradient(to top right, rgba(20,22,26,0.94) 0%, rgba(20,22,26,0.72) 45%, rgba(20,22,26,0.4) 100%)' }} />
        </>
      )}
      {/* reason ribbon */}
      <div className={action ? 'flex items-center justify-between mb-3 pr-16' : 'flex items-center justify-between mb-3'}>
        <span className="mono text-[10px] uppercase tracking-wider text-[var(--accent)] inline-flex items-center gap-1.5">
          {item.days_old != null ? <Sprout size={16} /> : <TrendingUp size={16} />}
          {item.trending_reason}
        </span>
        {item.days_old != null
          ? <span className="mono text-[10px] text-[var(--muted)]">{item.days_old}d old</span>
          : item.rank_pos != null && <span className="mono text-[10px] text-[var(--muted)]">#{item.rank_pos}</span>}
      </div>
      {/* name — deliberately NOT a heading: these cards sit above the page's
          h2 sections in DOM order, so an h3 here broke heading hierarchy
          (Lighthouse heading-order). A card title in a grid is list content,
          not document structure. */}
      <div className="text-[15px] font-semibold leading-snug text-[var(--foreground)] line-clamp-2 mb-2.5">
        {item.name}
      </div>
      {/* meta chips */}
      <div className="flex items-center gap-1.5 mono text-[10px] text-[var(--muted)] mb-3 flex-wrap">
        <span>{flag(item.country)} {item.country}</span>
        <span className="opacity-50">·</span>
        <span>{mapLabel(item.map)}</span>
        {item.first_person != null && (<><span className="opacity-50">·</span><span>{item.first_person ? '1PP' : '3PP'}</span></>)}
      </div>
      {/* Stats group anchored to the bottom via mt-auto on the big-stats row.
          With a fixed card height the variable space lives BETWEEN the identity
          group above and the stats group here, which reads as a deliberate gap
          rather than awkward emptiness mid-card. */}
      <div className="flex items-end justify-between gap-3 mb-3 mt-auto">
        <div>
          <div className="mono text-[22px] leading-none text-[var(--foreground)]">
            <span className="text-[var(--accent)]">{fmt(item.players)}</span>
            <span className="text-[var(--muted)] text-[14px]">/{fmt(item.max_players)}</span>
          </div>
          {item.queue > 0 && (
            <div className="mono text-[10px] text-[var(--accent)] mt-1">+{fmt(item.queue)} queued</div>
          )}
        </div>
        <div className="opacity-90">
          <Sparkline data={smoothTrend(item.pop_trend) as number[]} />
        </div>
      </div>
      {/* fill bar — track sits on the page background so it reads against the card */}
      <div className="h-1 rounded-full bg-[var(--background)] overflow-hidden mb-3">
        <div className="h-full bg-[var(--accent)]/70" style={{ width: `${Math.round(fill * 100)}%` }} />
      </div>
      {/* bottom row */}
      <div className="flex items-center justify-between mono text-[10px] text-[var(--muted)]">
        <span>last 24h population</span>
        {(() => {
          const measured = pingFor?.(item)
          if (measured != null) return (
            <span title="Measured from this PC just now">{measured}ms · <span className="text-[var(--foreground)]">{latencyWord(measured).toLowerCase()}</span></span>
          )
          const est = estimatePing?.(viewerCC, item.country)
          return est != null ? (
            <span title="Estimated from distance to your location">~{est}ms · <span className="text-[var(--foreground)]">{latencyWord(est).toLowerCase()}</span> est.</span>
          ) : null
        })()}
      </div>
    </button>
  )
  if (!action) return card
  return <div className="relative">{card}<div className="absolute top-3 right-3 z-[1]">{action(item)}</div></div>
}

export type DiscoverTab = 'trending' | 'upcoming'

export const TrendingStrip = memo(function TrendingStrip({ api, onOpen, region, you, viewerCC, countries, onRegionChange, onViewAll, estimatePing, pingFor, assetBase, imageFor, action, onItems }: {
  api: DiscoverApi
  onOpen: (id: number) => void
  region: string | null
  you: string | null
  viewerCC: string | null
  countries: { code: string; count: number }[]
  onRegionChange: (cc: string | null) => void
  onViewAll?: (tab: DiscoverTab) => void
  estimatePing?: TrendingCardProps['estimatePing']
  pingFor?: TrendingCardProps['pingFor']
  assetBase?: string
  imageFor?: TrendingCardProps['imageFor']
  action?: TrendingCardProps['action']
  // Called with the loaded items (launcher: measure their ping).
  onItems?: (items: TrendingItem[]) => void
}) {
  const [tab, setTab] = useState<DiscoverTab>('trending')
  const [page, setPage] = useState(0)
  useEffect(() => { setPage(0) }, [tab, region])     // reset page when switching tabs or region
  const data = useDiscover(api, tab, region)
  useEffect(() => { if (data && onItems) onItems(data) }, [data, onItems])
  const loading = data === null
  const all = data ?? []
  const perPage = 3
  const pages = Math.max(1, Math.ceil(all.length / perPage))
  const items = all.slice(page * perPage, page * perPage + perPage)
  const subtitle = tab === 'trending'
    ? 'quality-gated · momentum over the last 24h'
    : 'recently discovered · showing real growth · not yet ranked'
  return (
    <section className="mb-4">
      <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Tab toggle. The selected tab carries the accent + the section title. */}
          <div className="inline-flex rounded-full bg-[var(--surface-1)] p-1 gap-0.5">
            <button type="button" onClick={() => setTab('trending')}
              className={`mono text-[11px] uppercase tracking-wider px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors
                ${tab === 'trending' ? 'bg-[var(--brand-soft)] text-[var(--brand)] font-semibold ring-1 ring-[var(--brand)] [--icon-accent:var(--brand)]' : 'text-[var(--muted)] hover:text-[var(--foreground)]'}`}>
              <TrendingUp size={16} /> Trending now
            </button>
            <button type="button" onClick={() => setTab('upcoming')}
              className={`mono text-[11px] uppercase tracking-wider px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors
                ${tab === 'upcoming' ? 'bg-[var(--brand-soft)] text-[var(--brand)] font-semibold ring-1 ring-[var(--brand)] [--icon-accent:var(--brand)]' : 'text-[var(--muted)] hover:text-[var(--foreground)]'}`}>
              <Sprout size={16} /> New &amp; upcoming
            </button>
          </div>
          <p className="mono text-[11px] text-[var(--muted)] ml-1 inline-flex items-center gap-1.5 flex-wrap">
            <span>Servers</span>
            <RegionPicker region={region} you={you} countries={countries} onChange={onRegionChange} />
            <span className="opacity-50">·</span>
            <span>{subtitle}</span>
          </p>
        </div>
      </div>
      {loading
        ? <div className="mono text-[12px] text-[var(--muted)] py-6 px-4 rounded-xl bg-[var(--background-secondary)]">Loading…</div>
        : all.length === 0
        ? <div className="mono text-[12px] text-[var(--muted)] py-6 px-4 rounded-xl bg-[var(--background-secondary)] flex items-center gap-3 flex-wrap">
            <span>
              {region
                ? <>No {tab === 'trending' ? 'trending' : 'new'} servers in <span className="text-[var(--foreground)]">{flag(region)} {region}</span> right now.</>
                : (tab === 'trending'
                    ? 'No servers with momentum right now. Check back soon.'
                    : 'No new servers in the last 30 days with real activity yet.')}
            </span>
            {region && (
              <button type="button" onClick={() => onRegionChange(null)}
                className="mono text-[11px] uppercase tracking-wider rounded-full px-3 py-1.5 bg-[var(--background-tertiary)] text-[var(--foreground)] hover:bg-[var(--surface-1-hover)] transition-colors">
                Try 🌍 Anywhere
              </button>
            )}
          </div>
        : (
          <>
            <div key={`${tab}-${page}`} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((it, i) => <TrendingCard key={it.id} item={it} onOpen={onOpen} viewerCC={viewerCC} index={i} estimatePing={estimatePing} pingFor={pingFor} assetBase={assetBase} imageFor={imageFor} action={action} />)}
            </div>
            {/* Footer: View-all opens the dedicated /trending or /upcoming page;
                chevrons page through 3 cards at a time within the strip. */}
            <div className="flex items-center gap-2 mt-3">
              {onViewAll && (
                <button type="button" onClick={() => onViewAll(tab)}
                  className="mono text-xs uppercase tracking-wider rounded-full px-3.5 py-2 bg-[var(--background-tertiary)] text-[var(--foreground)] hover:bg-[var(--surface-1-hover)]">
                  View all
                </button>
              )}
              {pages > 1 && (
                <div className="ml-auto flex items-center gap-2">
                  <button type="button" aria-label="Previous page"
                    onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                    className="rounded-full p-2 bg-[var(--background-tertiary)] text-[var(--muted)] hover:text-[var(--foreground)] hover:bg-[var(--surface-1-hover)] disabled:opacity-40 disabled:hover:bg-[var(--background-tertiary)] disabled:cursor-not-allowed">
                    <ChevronLeft size={18} />
                  </button>
                  <span className="mono text-[11px] text-[var(--muted)] tabular-nums">{page + 1}/{pages}</span>
                  <button type="button" aria-label="Next page"
                    onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}
                    className="rounded-full p-2 bg-[var(--background-tertiary)] text-[var(--muted)] hover:text-[var(--foreground)] hover:bg-[var(--surface-1-hover)] disabled:opacity-40 disabled:hover:bg-[var(--background-tertiary)] disabled:cursor-not-allowed">
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
    </section>
  )
})
